import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

const NPX_PACKAGE = '@charmland/crush'

/**
 * Launcher for Charm Crush (`npm install -g @charmland/crush`).
 *
 * Interactive TUI is `crush` with no subcommand. `crush run` is one-shot —
 * never use it. Crush reads AGENTS.md / CRUSH.md from `--cwd`, so we write
 * those in a temp directory outside the candidate workspace.
 */
export class CrushLauncher implements ProviderLauncher {
  readonly name = 'crush'
  readonly displayName = 'Crush (Charm)'
  readonly pricingHint = '🆓 Charm Hyper (tier gratuit) ou clé API personnelle'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('crush', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable(NPX_PACKAGE)
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('crush', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-crush-'))
    const instructions = `# Evaluation Instructions

You are evaluating a candidate. Your job is to GUIDE them — propose approaches, help them think.

**You must not write code or fix things yourself.** The candidate must do the coding.

---

# Project Location
The actual project files are at: ${workdir}
You are NOT in the project directory right now. Navigate there when asked:
cd ${workdir}

${systemPrompt}
`
    await writeFile(join(agentDir, 'AGENTS.md'), instructions)
    await writeFile(join(agentDir, 'CRUSH.md'), instructions)

    const args: string[] = ['--cwd', agentDir]

    if (config.permissionMode === 'bypassPermissions' || config.permissionMode === 'acceptEdits') {
      args.push('--yolo')
    }

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? [NPX_PACKAGE, ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('crush', ['--version'])
      return 'crush'
    } catch {
      return 'npx'
    }
  }
}
